import React, { useState, useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { useCopyMode } from './copy/copyMode';
import { useMouseScrubVideo } from './useMouseScrubVideo';
const FOUNDER_PHOTO_URL = "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/image_14.png";
// The founder head-turn clip that gets scrubbed by horizontal mouse movement
// on desktop (moving the cursor rotates his head, like before). Falls back to
// the static photo on mobile / reduced-data. TODO: replace with the real
// founder head-turn asset — reusing the static photo below until then.
const FOUNDER_VIDEO_URL = '';
// A founder quote is built from inline "tokens" so we can mix plain runs of
// text with highlighted phrases. The `mark` tokens get the soft peach pill +
// orange text treatment.
type QuoteToken = {
  type: 'text';
  value: string;
} | {
  type: 'mark';
  value: string;
};
// Real founder copy, tuned per audience. The `mark` tokens are the phrases
// that get the soft orange pill + orange text treatment.
const QUOTE_BY_MODE: Record<string, QuoteToken[]> = {
  // Team / AI colleague leaning — philosophical: the problem is tool sprawl,
  // the answer is one intelligence layer over the stack, the payoff is the
  // next era of work where people do the work only they can do.
  current: [{
    type: 'text',
    value: 'The next era of work won\u2019t run on more tools. It\u2019ll run on '
  }, {
    type: 'mark',
    value: 'one layer of intelligence across your whole stack'
  }, {
    type: 'text',
    value: ' — connecting information that used to live in silos, so we make '
  }, {
    type: 'mark',
    value: 'data-driven decisions, faster'
  }, {
    type: 'text',
    value: '.'
  }],
  // Outcome / time-back leaning.
  outcome: [{
    type: 'text',
    value: "I'm done with my team losing whole days to "
  }, {
    type: 'mark',
    value: 'copy-pasting between tabs'
  }, {
    type: 'text',
    value: '. Giga just '
  }, {
    type: 'mark',
    value: 'gets it done while you sleep'
  }, {
    type: 'text',
    value: '.'
  }],
  // Founder / lean-team leaning — the one the user has been refining.
  founder: [{
    type: 'text',
    value: "The future isn't about more tools. It's about having "
  }, {
    type: 'mark',
    value: 'one AI brain for your entire stack'
  }, {
    type: 'text',
    value: '.'
  }]
};
export function NarrativeSection() {
  const {
    mode
  } = useCopyMode();
  const tokens = QUOTE_BY_MODE[mode] ?? QUOTE_BY_MODE.current;
  const sectionRef = useRef<HTMLElement>(null);
  // Drives the founder head-turn video from horizontal cursor movement.
  const founderVideoRef = useMouseScrubVideo<HTMLVideoElement>();
  // Plain-text version of the quote for screen readers + a11y label.
  const plainQuote = tokens.map(t => t.value).join('');
  const [isHovered, setIsHovered] = useState(false);
  const mouseX = useMotionValue(-100);
  const mouseY = useMotionValue(-100);
  const springX = useSpring(mouseX, {
    stiffness: 800,
    damping: 35
  });
  const springY = useSpring(mouseY, {
    stiffness: 800,
    damping: 35
  });
  const handleMouseMove = (e: React.MouseEvent) => {
    mouseX.set(e.clientX);
    mouseY.set(e.clientY);
  };
  return <section ref={sectionRef} onMouseMove={handleMouseMove} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-cta-invert="true" aria-label={`${plainQuote} — Tony Ennis, Founder`} className="relative w-full bg-[#EA580C] text-white py-20 md:py-28 overflow-hidden md:cursor-none" data-id="element-3789" data-mp="1ru7xm5ixdl1a">
      {/* Custom Cursor (Desktop only) */}
      <motion.div className="fixed top-0 left-0 z-50 pointer-events-none hidden md:block" style={{
      x: springX,
      y: springY,
      translateX: '-50%',
      translateY: '-50%',
      opacity: isHovered ? 1 : 0,
      scale: isHovered ? 1 : 0.5
    }} transition={{
      opacity: {
        duration: 0.2
      },
      scale: {
        duration: 0.2
      }
    }} data-id="element-3790" data-mp="1dv868q412s1f">
        {FOUNDER_VIDEO_URL ? <video ref={founderVideoRef} src={FOUNDER_VIDEO_URL} poster={FOUNDER_PHOTO_URL} muted playsInline preload="auto" aria-hidden="true" className="w-24 h-24 object-contain object-top drop-shadow-2xl" data-id="element-3791" data-mp="3fq7feban8fm9" /> : <img src={FOUNDER_PHOTO_URL} alt="" className="w-24 h-24 object-contain object-top drop-shadow-2xl" data-id="element-3792" data-mp="3oqvtt73y8hzo" />}
      </motion.div>

      {/* Halftone dot matrix over the orange field, like the reference. */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-60" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='1.5' height='1.5' fill='%23000000' fill-opacity='0.10'/%3E%3C/svg%3E\")",
      backgroundSize: '14px 14px'
    }} data-id="element-3793" data-mp="7c8gp1waeg52" />

      {/* A single, balanced, centered column: a small eyebrow, the founder
                           manifesto quote, then a grounded attribution row (real avatar +
                           name + title). No cursor gimmick — the composition stands on its
                           own as a deliberate brand moment before the dark closing CTA. */}
      <div className="relative z-10 max-w-[1000px] mx-auto px-6 md:px-10 flex flex-col items-center text-center" data-id="element-3794" data-mp="20ufwbiaey9cd">
        <span className="mb-7 md:mb-9 text-[11px] font-mono font-bold tracking-[0.35em] uppercase text-white/80" data-id="element-3795" data-mp="1wudr4z5gyxsc">
          Why we’re building this
        </span>

        {/* Display quote with inline peach-pill highlights. */}
        <h2 aria-hidden="true" className="text-[26px] leading-[1.7] sm:text-4xl sm:leading-[1.45] md:text-5xl md:leading-[1.25] font-title font-medium tracking-tight text-balance max-w-4xl" data-id="element-3796" data-mp="2olbi9ys6uabp">
          <span className="text-white/90" data-id="element-3797" data-mp="2fjbzp21iyy6n">&ldquo;</span>
          {tokens.map((token, i) => {
          if (token.type === 'mark') {
            return <span key={i} className="inline bg-[#FBDDC9] text-[#EA580C] font-medium rounded-full px-2.5 py-0.5 md:px-5 md:py-1 mx-0.5 md:mx-1 decoration-clone box-decoration-clone" data-id="element-3798" data-mp="144opcjkxqtpo">
                  {token.value}
                </span>;
          }
          return <span key={i} data-id="element-3799" data-mp="1akf47ww157w9" data-mpi={i}>{token.value}</span>;
        })}
          <span className="text-white/90" data-id="element-3800" data-mp="wlp6cf1dz46u">&rdquo;</span>
        </h2>

        {/* Grounded attribution — name and title. The avatar acts as a custom
         cursor on desktop, so it's only shown inline here on mobile. */}
        <div className="mt-10 md:mt-12 flex flex-row md:flex-col items-center gap-4 md:gap-1.5" data-id="element-3801" data-mp="mubcams7ewl3">
          <img src={FOUNDER_PHOTO_URL} alt="Tony Ennis, Founder of Giga" className="md:hidden shrink-0 w-14 h-14 object-contain object-top drop-shadow-md" data-id="element-3802" data-mp="rn4gn9jrg2wn" />

          <div className="text-left md:text-center" data-id="element-3803" data-mp="hw9hj4lf18ts">
            <div className="text-base md:text-lg font-bold text-white leading-tight" data-id="element-3804" data-mp="hx1fgm2hjin8">
              Tony Ennis
            </div>
            <div className="text-[11px] md:text-xs font-mono font-bold tracking-[0.28em] uppercase text-white/70 mt-0.5" data-id="element-3805" data-mp="3yl6spl2bi2g">
              Founder, Giga
            </div>
          </div>
        </div>
      </div>
    </section>;
}